(() => {
  if (window.__ottoCartWrite) return;
  const sections = ['shopping-cart', 'cart-amount', 'cart-recommendations'];
  const controls = 'shopping-cart button, shopping-cart input, [data-module="product-form"] button[type="submit"], [data-gap-add], [data-sticky-atc-submit], [data-gift-add-to-cart], #cart-dialog button[name="checkout"]';
  let pending = false;
  let writeRevision = 0;
  let readPromise = null;
  let lastRead = 0;
  let drawerRevision = 0;
  window.addEventListener('cart:toggle', () => { drawerRevision++; }, true);

  const drawerIsOpen = drawer => Boolean(drawer?.classList.contains('translate-x-0'));

  const request = async (url, options = {}) => {
    const controller = new AbortController();
    let timeout;
    try {
      return await Promise.race([
        (async () => {
          const response = await fetch(url, { ...options, signal: controller.signal });
          const data = await response.json();
          if (!response.ok || data?.status >= 400) {
            const error = new Error('Cart request failed');
            error.description = typeof data?.description === 'string' ? data.description : '';
            throw error;
          }
          return data;
        })(),
        new Promise((resolve, reject) => {
          timeout = setTimeout(() => {
            controller.abort();
            reject(new Error('Cart request timed out'));
          }, options.method === 'POST' ? 15000 : 5000);
        }),
      ]);
    } finally {
      clearTimeout(timeout);
    }
  };

  const refresh = async (rendered, mayReplace = () => true) => {
    const complete = (data) => sections.every(name => typeof data?.[name] === 'string' && data[name].includes(`<${name}`));
    if (!complete(rendered)) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sections.join(','));
      rendered = await request(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    }
    if (!complete(rendered) || typeof window.__ottoReplaceCartSections !== 'function') {
      throw new Error('Cart sections unavailable');
    }
    if (!mayReplace()) return false;
    window.__ottoReplaceCartSections(rendered);
    lastRead = Date.now();
    return true;
  };

  const reconcile = (force = false) => {
    if (pending || readPromise || (!force && Date.now() - lastRead < 10000)) return readPromise;
    const revision = writeRevision;
    readPromise = refresh(null, () => !pending && revision === writeRevision)
      .catch(() => { window.__ottoReportUIError?.('cart_read'); return false; })
      .finally(() => { readPromise = null; });
    return readPromise;
  };
  const cartVisible = () => drawerIsOpen(document.querySelector('#cart-dialog'))
    || Boolean(document.querySelector('main shopping-cart'));
  window.addEventListener('pageshow', event => {
    if (event.persisted || cartVisible()) reconcile(true);
  });
  window.addEventListener('cart:toggle', () => {
    requestAnimationFrame(() => { if (cartVisible()) reconcile(); });
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && cartVisible()) reconcile(true);
  });
  window.addEventListener('storage', event => {
    if (event.key === 'otto:cart-revision' && cartVisible()) reconcile(true);
  });

  // One in-flight write across product forms, recommendations, upsell and line controls.
  // A failed/ambiguous POST is NEVER retried: reconcile with a read instead.
  window.__ottoCartWrite = async ({ source, action, body, kind = 'add', focusSelector, openOnSuccess = false, opener = source }) => {
    if (pending) return false;
    pending = true;
    writeRevision++;
    const drawer = source.closest('#cart-dialog');
    const pageDrawer = document.querySelector('#cart-dialog');
    const openingRevision = drawerRevision;
    const mayOpen = openOnSuccess && pageDrawer && !drawerIsOpen(pageDrawer);
    const cart = source.closest('shopping-cart') || document.querySelector('shopping-cart');
    const labels = cart?.dataset || {};
    const host = drawer || source.closest('[data-module="product-form"]') || cart?.parentElement || source.parentElement;
    let status = host.querySelector('[data-cart-status]');
    if (!status) {
      status = document.createElement('p');
      status.dataset.cartStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('tabindex', '-1');
      host.prepend(status);
    }
    status.className = 'sr-only';
    status.textContent = '';
    const hadFocus = source.contains(document.activeElement);
    const states = Array.from(document.querySelectorAll(controls), element => [element, element.disabled]);
    states.forEach(([element]) => { element.disabled = true; });
    source.setAttribute('aria-busy', 'true');
    let accepted = false;
    let refreshed = false;
    try {
      const url = new URL(action, window.location.origin);
      if (!url.pathname.endsWith('.js')) url.pathname += '.js';
      const headers = { Accept: 'application/json' };
      if (body instanceof FormData) {
        body.set('sections', sections.join(','));
        body.set('sections_url', window.location.pathname);
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({ ...body, sections: sections.join(','), sections_url: window.location.pathname });
      }
      const result = await request(url, { method: 'POST', headers, body });
      accepted = true;
      await refresh(result.sections);
      refreshed = true;
      try { window.localStorage?.setItem('otto:cart-revision', String(Date.now())); } catch (_) {}
      status.textContent = kind === 'add' ? labels.cartAdded : labels.cartUpdated;
      if (mayOpen && openingRevision === drawerRevision && !drawerIsOpen(pageDrawer)) {
        window.dispatchEvent(new CustomEvent('cart:toggle', { detail: { element: opener, skipCartDefer: true } }));
      }
    } catch (error) {
      window.__ottoReportUIError?.('cart_write');
      if (!accepted) {
        try { await refresh(); refreshed = true; } catch (_) { /* Keep visible error and allow a manual retry. */ }
      }
      status.className = 'text-sm px-4 py-2';
      status.textContent = error.description || (accepted
        ? labels.cartRefreshError
        : kind === 'add' ? labels.cartAddError : labels.cartUpdateError);
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'btn-secondary ml-2 px-3 py-2';
      retry.textContent = labels.cartRefreshLabel;
      retry.addEventListener('click', async () => {
        if (pending) return;
        pending = true;
        retry.disabled = true;
        try {
          await refresh();
          status.textContent = labels.cartUpdated;
          status.className = 'sr-only';
          (drawer?.querySelector('.cart-drawer__header [data-element="disclosure-button"]') || status).focus({ preventScroll: true });
        } catch (_) {
          retry.disabled = false;
        } finally { pending = false; }
      });
      status.append(retry);
    } finally {
      pending = false;
      source.removeAttribute('aria-busy');
      states.forEach(([element, disabled]) => { element.disabled = disabled; });
      // The user may have deliberately closed the drawer while the request ran.
      // Never steal focus back into a closed drawer.
      if (hadFocus && !source.isConnected && (!drawer || (!drawer.hasAttribute('inert') && !drawer.classList.contains('translate-x-full')))) {
        const target = (focusSelector && host.querySelector(focusSelector))
          || drawer?.querySelector('.cart-drawer__header [data-element="disclosure-button"]')
          || status;
        target.focus({ preventScroll: true });
      }
    }
    return accepted && refreshed;
  };

  window.__ottoSubmitProductForm = async (form, button, body = null) => {
    if (pending || !form.reportValidity()) return false;
    if (!body) body = new FormData(form);
    if (body instanceof FormData && !body.get('id')) return false;
    const added = await window.__ottoCartWrite({ source: form, action: form.action, body,
      opener: button || form.querySelector('button[type="submit"]'), openOnSuccess: true });
    if (added) form.closest('#product-drawer')?.hidePopover?.();
    return added;
  };

  // Defer the write until document-level gift-option handlers have run;
  // the native product-form target handler must not start a second POST.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('[data-module="product-form"]') || form.closest('cart-recommendations')) return;
    event.preventDefault();
    event.stopPropagation();
    setTimeout(() => {
      if (!event.ottoGiftHandled) window.__ottoSubmitProductForm(form, event.submitter);
    }, 0);
  }, true);

  const initializedAgeForms = new WeakSet();
  let ageFormSequence = 0;
  const initializeAgeForms = () => {
    document.querySelectorAll('cart-buttons:has([data-age-confirmation])').forEach(group => {
      if (initializedAgeForms.has(group)) return;
      const form = group.querySelector('[data-checkout-form]');
      if (!form) return;
      initializedAgeForms.add(group);
      form.id = `checkout-age-${++ageFormSequence}`;
      group.querySelectorAll('[form]').forEach(control => control.setAttribute('form', form.id));
      const error = group.querySelector('[data-age-error]');
      error.id = `${form.id}-error`;
      group.querySelector('[data-age-confirmation]').setAttribute('aria-describedby', error.id);
    });
  };
  initializeAgeForms();
  new MutationObserver(initializeAgeForms).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('cart:updated', initializeAgeForms);

  const requireAgeConfirmation = (group, event) => {
    const checkbox = group?.querySelector('[data-age-confirmation]');
    if (!checkbox || checkbox.checked) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    group.querySelector('[data-age-error]').hidden = false;
    checkbox.setAttribute('aria-invalid', 'true');
    checkbox.focus();
    return true;
  };

  window.addEventListener('click', event => {
    const button = event.target.closest?.('cart-buttons button[name="checkout"]');
    if (button) requireAgeConfirmation(button.closest('cart-buttons'), event);
  }, true);

  document.addEventListener('change', event => {
    if (!event.target.matches?.('[data-age-confirmation]')) return;
    const checkbox = event.target;
    checkbox.closest('cart-buttons').querySelector('[data-age-error]').hidden = checkbox.checked;
    checkbox.setAttribute('aria-invalid', String(!checkbox.checked));
  });

  window.addEventListener('submit', event => {
    const form = event.target;
    if (form.matches?.('cart-buttons form[data-checkout-form]')) {
      const group = form.closest('cart-buttons');
      if (requireAgeConfirmation(group, event)) return;
      const terms = group.querySelector('input[name="terms"]');
      if (pending || (terms && !terms.checked)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!pending) {
          group.querySelector('#terms-error')?.classList.remove('hidden');
          terms.focus();
        }
      }
      return;
    }
    if (!(form instanceof HTMLFormElement) || !form.closest('cart-recommendations')
      || !form.closest('[data-module="product-form"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pending || !form.reportValidity()) return;
    const body = new FormData(form);
    if (!body.get('id')) return;
    const variant = CSS.escape(String(body.get('id')));
    return window.__ottoCartWrite({ source: form, action: form.action, body,
      focusSelector: `cart-recommendations form:has(input[name="id"][value="${variant}"]) button[type="submit"]:not(:disabled)` });
  }, true);

  const changeLine = (input, source, quantity, focusSelector) => {
    const line = input.closest('[data-element="cart-item"]');
    const cart = input.closest('shopping-cart');
    if (!line?.dataset.lineKey || !Number.isInteger(quantity) || quantity < 0) return;
    const body = new FormData();
    body.set('id', line.dataset.lineKey);
    body.set('quantity', String(quantity));
    return window.__ottoCartWrite({ source, action: cart.dataset.changeUrl, body, kind: 'change', focusSelector });
  };

  window.addEventListener('click', event => {
    const button = event.target.closest?.('shopping-cart [data-element="remove-btn"], shopping-cart button[name="increment-button"], shopping-cart button[name="decrement-button"]');
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pending || button.disabled) return;
    const line = button.closest('[data-element="cart-item"]');
    const input = line?.querySelector('[data-element="quantity-selector"]');
    if (!input) return;
    const removing = button.dataset.element === 'remove-btn';
    const step = Number(input.step) || 1;
    const current = Number(line.dataset.quantity);
    const quantity = removing ? 0 : Math.max(Number(input.min) || 0,
      Math.min(input.max === '' ? Infinity : Number(input.max), current + (button.name === 'increment-button' ? step : -step)));
    if (quantity === current) return;
    const selector = `[data-element="cart-item"][data-line-key="${CSS.escape(line.dataset.lineKey)}"] [data-element="quantity-selector"]`;
    return changeLine(input, button, quantity, removing ? null : selector);
  }, true);

  // Capture also handles the legacy number-input's non-bubbling change events.
  window.addEventListener('change', event => {
    const input = event.target;
    if (!input.matches?.('shopping-cart [data-element="quantity-selector"]')) return;
    event.stopImmediatePropagation();
    const line = input.closest('[data-element="cart-item"]');
    if (pending || !input.reportValidity()) { input.value = line.dataset.quantity; return; }
    const quantity = Number(input.value);
    if (quantity === Number(line.dataset.quantity)) return;
    const selector = `[data-element="cart-item"][data-line-key="${CSS.escape(line.dataset.lineKey)}"] [data-element="quantity-selector"]`;
    return changeLine(input, input, quantity, selector);
  }, true);
})();
